import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Autocomplete,
  MenuItem
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as ApproveIcon,
  Print as PrintIcon,
  PlayArrow as PlayIcon,
  ArrowBack as BackIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Medication as MedIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  EventAvailable as FollowUpIcon,
  Description as ReportIcon,
  NoteAdd as AddReportIcon
} from '@mui/icons-material';
import ReportUpload, { reportKindLabel } from '../components/ReportUpload';
import { openReportFile } from './Reports';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// A single heartbeat trace, drawn twice for the finalize overlay: once faint as a
// track, once as the sweeping line that runs along it.
const ECG_PATH = 'M4 40 H64 l9 -26 l11 52 l11 -42 l9 16 h22 l8 -12 l10 22 h88';

// How to take a medicine. The keys match the backend (app/core/prescriptions.py).
const RX_TIMINGS = [
  { value: '', label: 'Not specified' },
  { value: 'before_food', label: 'Before food' },
  { value: 'after_food', label: 'After food' },
  { value: 'with_food', label: 'With food' },
  { value: 'empty_stomach', label: 'On an empty stomach' },
  { value: 'bedtime', label: 'At bedtime' },
];
const RX_TIMING_LABEL = Object.fromEntries(RX_TIMINGS.filter((t) => t.value).map((t) => [t.value, t.label]));
const RX_FREQUENCIES = [
  'Once a day', 'Twice a day', 'Three times a day', 'Four times a day',
  'Every 8 hours', 'Every 12 hours', 'At night only', 'When needed',
  '1-0-0', '0-0-1', '1-0-1', '1-1-1',
];
const RX_FIELDS = ['dose', 'frequency', 'timing', 'duration', 'notes'];
const EMPTY_RX = { medication: '', dose: '', frequency: '', timing: '', duration: '', notes: '' };

/** A line saved before the structured fields existed has only free-text `sig`:
 *  open it with that text under "Other instructions" so nothing is lost. */
function toEditableRx(rx) {
  const structured = RX_FIELDS.some((k) => k in (rx || {}));
  return { ...EMPTY_RX, ...rx, notes: structured ? (rx.notes || '') : (rx?.sig || '') };
}

/** The read-only view: each part of the directions as its own chip. */
function RxDirections({ rx }) {
  const structured = RX_FIELDS.some((k) => rx[k]);
  if (!structured) {
    return rx.sig ? <Typography variant="body2" color="text.secondary">{rx.sig}</Typography> : null;
  }
  const chips = [
    rx.dose,
    rx.frequency,
    RX_TIMING_LABEL[rx.timing],
    rx.duration && (/^(for |until|till|ongoing|continue)/i.test(rx.duration) ? rx.duration : `For ${rx.duration}`),
  ].filter(Boolean);
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {chips.map((c) => <Chip key={c} label={c} size="small" variant="outlined" />)}
      </Box>
      {rx.notes && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{rx.notes}</Typography>}
    </Box>
  );
}

// Follow-up: quick picks counted from the day of the visit.
const FOLLOW_UP_PICKS = [
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
  { label: '3 months', days: 91 },
];
function addDays(isoDay, days) {
  const d = new Date(`${isoDay}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function longDay(isoDay) {
  return new Date(`${isoDay}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

/** Parse "1. ... 2. ..." from text into points (fallback when no structured points). */
function parsePointsFromText(text) {
  if (!text || typeof text !== 'string') return [];
  const matches = text.match(/\d+\.\s*[^\n]+/g);
  return matches ? matches.map((m) => ({ text: m.trim() })) : [];
}

/** Same source as the read-only list: API points first, else parse SOAP text. */
function getPointsForSectionFromNote(noteData, section) {
  if (!noteData) return [];
  const points = noteData.custom_fields?.[`${section}_points`];
  if (Array.isArray(points) && points.length > 0) return points;
  const text = noteData.soap?.[section];
  return parsePointsFromText(text);
}

/** Hold-to-talk button that dictates into one field. Recording is per-field so the
 *  transcript lands where the provider is looking, not in whatever had focus. */
function DictateButton({ field, busy, activeField, onText, onRecord, available = true }) {
  // No AI sessions left: no dictation either — it is AI, and offering a button that can
  // only be refused is worse than not offering it.
  if (!available) return null;
  const isThis = busy && activeField === field;
  return (
    <Tooltip title={isThis ? 'Stop and transcribe' : 'Dictate this field'}>
      <span>
        <IconButton
          size="small"
          color={isThis ? 'error' : 'default'}
          disabled={busy && !isThis}
          onClick={() => onRecord(field, onText)}
          sx={{ mt: 0.5 }}
        >
          {isThis ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function ReviewNote() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSoap, setEditedSoap] = useState({});
  const [editedIcd10Codes, setEditedIcd10Codes] = useState([]);
  const [openAddIcd10Dialog, setOpenAddIcd10Dialog] = useState(false);
  const [newIcd10Code, setNewIcd10Code] = useState('');
  const [newIcd10Desc, setNewIcd10Desc] = useState('');
  const [isEditingPatient, setIsEditingPatient] = useState(false);
  const [editedPatient, setEditedPatient] = useState({});
  const [editedPrescriptions, setEditedPrescriptions] = useState([]);
  const [editedFollowUp, setEditedFollowUp] = useState('');
  const [patientReports, setPatientReports] = useState([]);
  const [addingReport, setAddingReport] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [dictatingField, setDictatingField] = useState(null);
  const dictateRecorderRef = useRef(null);
  const [finalizing, setFinalizing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playingSegment, setPlayingSegment] = useState(null);
  const audioRef = useRef(null);
  const autoEditedRef = useRef(false);
  const audioUrlRef = useRef(null);

  const fetchNote = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/clinical/notes/${sessionId}`);
      setNote(response.data);
      setEditedSoap(response.data.soap);
      // A visit written by hand opens straight into the editor, empty, on first load —
      // there is nothing to review yet, only sections to fill in.
      const soap = response.data.soap || {};
      if (response.data.is_manual && !autoEditedRef.current
          && response.data.status !== 'finalized' && response.data.can_edit !== false
          && !['subjective', 'objective', 'assessment', 'plan'].some((k) => (soap[k] || '').trim())) {
        autoEditedRef.current = true;
        setIsEditing(true);
      }
      setEditedIcd10Codes(response.data.icd10_codes || []);
      setEditedPrescriptions((response.data.prescriptions_draft || []).map(toEditableRx));
      setEditedFollowUp(response.data.follow_up_date || '');
      setEditedPatient(response.data.patient || {});
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load note');
      setLoading(false);
    }
  }, [sessionId]);

  const fetchTranscript = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/scribe/session/${sessionId}/transcript`);
      // The transcript originates as AI output, so its shape is not guaranteed the way
      // our own responses are. It is normally an array of segments — but anything else
      // used to reach `.map` below and take the whole review screen down with it, and a
      // provider staring at a blank page cannot finalise the visit at all. Degrade to
      // "no transcript shown" instead; the note is what they are signing off.
      const segments = response.data?.segments;
      setTranscript(Array.isArray(segments) ? segments : []);
    } catch (error) {
      console.error('Failed to load transcript');
    }
  }, [sessionId]);

  useEffect(() => {
    fetchNote();
  }, [fetchNote]);

  // A note written by hand has no recording, so there is no transcript or audio to ask
  // for — asking would only fail. Both wait until the note says which kind it is.
  const isManual = Boolean(note?.is_manual);
  useEffect(() => {
    if (note?.session_id && !isManual) fetchTranscript();
  }, [note?.session_id, isManual, fetchTranscript]);

  // Load audio for playback when note exists
  // Finalizing deletes the recording, so a finalized visit has none to fetch.
  const noteFinalized = note?.status === 'finalized';
  useEffect(() => {
    if (!sessionId || !note?.session_id || isManual || noteFinalized) return;
    axios
      .get(`${API_URL}/api/v1/scribe/session/${sessionId}/audio`, { responseType: 'blob' })
      .then((res) => {
        if (res.data) {
          const url = URL.createObjectURL(res.data);
          audioUrlRef.current = url;
          setAudioUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, [sessionId, note?.session_id, isManual, noteFinalized]);

  const getPointsForSection = (section) => getPointsForSectionFromNote(note, section);

  /** When saving, rebuild points from edited lines; keep transcript timestamps by index when possible. */
  const buildPointsFromEditedSection = (section) => {
    const text = editedSoap[section] || '';
    const originalPoints = getPointsForSectionFromNote(note, section);
    const lines = text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
    return lines.map((line, i) => {
      const orig = originalPoints[i];
      if (orig && typeof orig === 'object') {
        return { ...orig, text: line };
      }
      return { text: line };
    });
  };

  const handleSavePatientEdits = async () => {
    if (!editedPatient.patient_id) return;
    try {
      const payload = {
        full_name: editedPatient.full_name,
        date_of_birth: editedPatient.dob || undefined,
        bp: editedPatient.bp || undefined,
        weight: editedPatient.weight || undefined,
        height: editedPatient.height || undefined,
        temperature: editedPatient.temperature || undefined,
        pulse: editedPatient.pulse ? parseInt(editedPatient.pulse, 10) : undefined,
        respiratory_rate: editedPatient.respiratory_rate ? parseInt(editedPatient.respiratory_rate, 10) : undefined,
      };
      await axios.put(`${API_URL}/api/v1/clinical/patients/${editedPatient.patient_id}`, payload);
      toast.success('Patient details updated');
      setIsEditingPatient(false);
      fetchNote();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg || 'Invalid input')
        : (typeof detail === 'string' ? detail : error.message);
      toast.error(`Failed to save patient details: ${msg}`);
    }
  };

  const updatePrescription = (idx, field, value) => {
    setEditedPrescriptions((prev) => prev.map((rx, i) => (i === idx ? { ...rx, [field]: value } : rx)));
  };

  /** Dictation appends rather than replaces, so a correction spoken after a typed
   *  start doesn't wipe what's already there. */
  const appendToPrescription = (idx, field, text) => {
    setEditedPrescriptions((prev) => prev.map((rx, i) => {
      if (i !== idx) return rx;
      const existing = (rx[field] || '').trim();
      return { ...rx, [field]: existing ? `${existing} ${text}` : text };
    }));
  };

  const addPrescription = () => setEditedPrescriptions((prev) => [...prev, { ...EMPTY_RX }]);
  const removePrescription = (idx) => setEditedPrescriptions((prev) => prev.filter((_, i) => i !== idx));

  /** Record until the provider taps stop, then transcribe into the given field. */
  const dictateInto = async (field, onText) => {
    if (dictating && dictatingField === field) {
      try { dictateRecorderRef.current?.stop(); } catch (_) {}
      return;
    }
    if (dictating) return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error('Could not access the microphone — type it instead');
      return;
    }
    const rec = new MediaRecorder(
      stream,
      MediaRecorder.isTypeSupported('audio/webm') ? { mimeType: 'audio/webm' } : undefined,
    );
    dictateRecorderRef.current = rec;
    const parts = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) parts.push(e.data); };
    rec.onstop = async () => {
      try { stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
      const blob = new Blob(parts, { type: rec.mimeType || 'audio/webm' });
      try {
        const fd = new FormData();
        fd.append('audio', blob, 'dictation.webm');
        const res = await axios.post(`${API_URL}/api/v1/clinical/notes/${sessionId}/dictate`, fd);
        if (res.data?.text) onText(res.data.text);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Could not transcribe — type it instead');
      } finally {
        setDictating(false);
        setDictatingField(null);
      }
    };
    rec.start();
    setDictating(true);
    setDictatingField(field);
  };

  /** This patient's reports at this office; the ones linked to this visit come first. */
  const loadReports = useCallback(async (patientId) => {
    if (!patientId) return;
    try {
      const res = await axios.get(`${API_URL}/api/v1/reports`, { params: { patient_id: patientId } });
      setPatientReports(res.data.reports || []);
    } catch {
      setPatientReports([]);
    }
  }, []);
  const reportsPatientId = note?.patient?.patient_id;
  useEffect(() => { loadReports(reportsPatientId); }, [loadReports, reportsPatientId]);

  const handleSaveEdits = async () => {
    try {
      await axios.put(`${API_URL}/api/v1/clinical/notes/${sessionId}`, {
        ...editedSoap,
        icd10_codes: editedIcd10Codes,
        follow_up_date: editedFollowUp || null,
        prescriptions_draft: editedPrescriptions
          .filter((rx) => (rx.medication || '').trim())
          .map((rx) => ({
            medication: rx.medication.trim(),
            ...Object.fromEntries(RX_FIELDS.map((k) => [k, (rx[k] || '').trim()])),
          })),
        custom_fields: {
          ...(note.custom_fields || {}),
          assessment_points: buildPointsFromEditedSection('assessment'),
          plan_points: buildPointsFromEditedSection('plan'),
        },
      });
      toast.success('Edits saved');
      setIsEditing(false);
      fetchNote();
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : error.message;
      toast.error(`Failed to save edits: ${msg}`);
      return false;
    }
  };

  /** Add dictated text to the end of a note section. */
  const appendToSection = (section, text) => {
    setEditedSoap((prev) => {
      const existing = (prev[section] || '').trim();
      return { ...prev, [section]: existing ? `${existing}\n${text}` : text };
    });
  };

  /** Prep editable text from structured points, then enter edit mode. */
  const enterEditMode = () => {
    setEditedSoap((prev) => {
      const next = { ...prev };
      ['assessment', 'plan'].forEach((section) => {
        const pts = getPointsForSectionFromNote(note, section);
        if (pts.length > 0) {
          next[section] = pts.map((p) => p.text).join('\n');
        }
      });
      return next;
    });
    setIsEditing(true);
  };

  /** Discard unsaved edits and restore from the loaded note. */
  const cancelEdits = () => {
    setEditedSoap(note.soap);
    setEditedIcd10Codes(note.icd10_codes || []);
    setEditedPrescriptions((note.prescriptions_draft || []).map(toEditableRx));
    setEditedFollowUp(note.follow_up_date || '');
    setIsEditing(false);
  };

  const handleRemoveIcd10 = (idx) => {
    setEditedIcd10Codes((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddIcd10 = () => {
    const code = newIcd10Code.trim();
    const desc = newIcd10Desc.trim();
    if (!code) {
      toast.error('Code is required');
      return;
    }
    setEditedIcd10Codes((prev) => [...prev, { code, description: desc || code }]);
    setNewIcd10Code('');
    setNewIcd10Desc('');
    setOpenAddIcd10Dialog(false);
  };

  const handleFinalize = async () => {
    const soapNow = isEditing ? editedSoap : (note?.soap || {});
    if (!['subjective', 'objective', 'assessment', 'plan'].some((k) => (soapNow[k] || '').trim())) {
      toast.error('The note is empty — write or dictate at least one section first');
      return;
    }
    if (!window.confirm(
      'Finalize this visit?\n\n' +
      'This approves the note, generates the patient summary and shares it with the patient, ' +
      'permanently DELETES the audio recording, LOCKS the note from further edits, and closes ' +
      'the visit. It cannot be undone.\n\n' +
      'Make any remaining edits before finalizing.'
    )) return;
    // Unsaved edits would otherwise be lost: finalizing locks the note as it is stored.
    if (isEditing && !(await handleSaveEdits())) return;
    setFinalizing(true);
    try {
      await axios.post(`${API_URL}/api/v1/clinical/notes/${sessionId}/finalize`);
      const patientId = note?.patient?.patient_id;
      // The summary is written and emailed after the response — saying "sent" here
      // would be claiming something that has not happened yet.
      toast.success('Note finalized — the patient summary is on its way');
      if (patientId) {
        navigate('/patients', { state: { openPatientId: patientId, openTab: 1 } });
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize');
      setFinalizing(false);   // stays up on success — the page is navigating away
    }
  };

  /**
   * MediaRecorder webm/opus blobs ship without a duration in their header, so the
   * browser reports `duration === Infinity` and treats the clip as a LIVE stream —
   * which breaks seeking, clips the final word on playback, and shows it as "live".
   * Forcing a seek to a huge time makes the browser index the whole file and
   * compute the real duration; we then reset to the start. (Items 8 & 11.)
   */
  const handleAudioMetadata = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.duration === Infinity || Number.isNaN(audio.duration)) {
      const onTimeUpdate = () => {
        audio.removeEventListener('timeupdate', onTimeUpdate);
        audio.currentTime = 0;
      };
      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.currentTime = 1e101;
    }
  };

  const playSegment = (start, end) => {
    if (!audioUrl || !audioRef.current) {
      toast.error('Audio not loaded');
      return;
    }
    const audio = audioRef.current;
    const endTime = typeof end === 'number' ? end : audio.duration;

    if (typeof endTime !== 'number' || !Number.isFinite(endTime)) {
      toast.error('Audio is still loading. Please try again in a moment.');
      return;
    }

    setPlayingSegment({ start, end: endTime });
    audio.currentTime = Number(start);
    audio.play();
    const stopAtEnd = () => {
      if (audio.currentTime >= endTime) {
        audio.pause();
        setPlayingSegment(null);
        audio.removeEventListener('timeupdate', stopAtEnd);
      }
    };
    audio.addEventListener('timeupdate', stopAtEnd);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography>Loading note...</Typography>
      </Box>
    );
  }

  if (!note) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography color="error">Note not found</Typography>
        <Button onClick={() => navigate('/patients')}>Back to Patients</Button>
      </Box>
    );
  }

  // No note yet — it is being written, or the visit was recorded and never sent to the
  // AI. There is nothing to review, so say so and offer the way on, rather than render
  // a review of nothing (which used to crash the page).
  if (!note.soap) {
    const writing = note.status === 'processing';
    return (
      <Box sx={{ textAlign: 'center', py: 10, maxWidth: 520, mx: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          {writing ? 'The note is still being written' : "This visit doesn't have a note yet"}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {writing
            ? 'Give it a moment, then refresh.'
            : note.has_audio
              ? 'Its recording is saved. Open it to generate the note.'
              : 'Nothing was recorded. Open the visit to record it, or discard it.'}
        </Typography>
        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Button variant="outlined" onClick={() => navigate('/patients')}>Back to Patients</Button>
          {writing ? (
            <Button variant="contained" onClick={fetchNote}>Refresh</Button>
          ) : note.patient_id && (
            <Button variant="contained" onClick={() => navigate(`/scribe/${note.patient_id}`)}>
              Open the visit
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  // Finalizing approves the note, so a finalized visit reads as Approved. Sessions
  // approved under the old two-step flow show the same badge.
  const isFinalized = note.status === 'finalized';
  const isApproved = isFinalized || note.status === 'approved';
  // Only the clinician who recorded the visit may change or sign it; a colleague or an
  // administrator reads it. A finalized note is locked for everyone.
  const canEdit = note.can_edit !== false && !isFinalized;
  const canDictate = canEdit && note.can_dictate !== false;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/patients')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight={600}>
          {note.is_manual ? 'Visit Note' : 'Review Note'}
        </Typography>
        {note.is_manual && (
          <Chip size="small" variant="outlined" label="Written by hand — no recording" sx={{ ml: 1.5 }} />
        )}
        <Box sx={{ flexGrow: 1 }} />
        {isEditing ? (
          <>
            <Button variant="outlined" onClick={cancelEdits}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveEdits}
            >
              Save changes
            </Button>
          </>
        ) : (
          isApproved && (
            <>
              <Chip color="success" icon={<ApproveIcon />} label="Approved" sx={{ fontWeight: 600 }} />
              {/* The patient's copy on the practice letterhead — its own tab, so the
                  review stays where it is. */}
              <Button variant="outlined" size="small" startIcon={<PrintIcon />}
                onClick={() => window.open(`/print/visit/${sessionId}`, '_blank', 'noopener')}>
                Print for patient
              </Button>
            </>
          )
        )}
      </Box>

      {/* Patient Vitals Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Patient Information"
          action={
            note.patient?.patient_id && (
              <>
                {isEditingPatient ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={() => { setIsEditingPatient(false); setEditedPatient(note.patient); }}>
                      Cancel
                    </Button>
                    <Button startIcon={<SaveIcon />} onClick={handleSavePatientEdits} size="small" variant="contained">
                      Save
                    </Button>
                  </Box>
                ) : (
                  <Button startIcon={<EditIcon />} onClick={() => setIsEditingPatient(true)} size="small">
                    Edit
                  </Button>
                )}
              </>
            )
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Name</Typography>
              {isEditingPatient ? (
                <TextField
                  fullWidth
                  size="small"
                  value={editedPatient.full_name || ''}
                  onChange={(e) => setEditedPatient({ ...editedPatient, full_name: e.target.value })}
                />
              ) : (
                <Typography variant="body1" fontWeight={500}>{note.patient.name}</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">DOB</Typography>
              {isEditingPatient ? (
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={editedPatient.dob || ''}
                  onChange={(e) => setEditedPatient({ ...editedPatient, dob: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <Typography variant="body1">{note.patient.dob}</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle2" color="text.secondary">BP</Typography>
              {isEditingPatient ? (
                <TextField
                  fullWidth
                  size="small"
                  value={editedPatient.bp || ''}
                  onChange={(e) => setEditedPatient({ ...editedPatient, bp: e.target.value })}
                />
              ) : (
                <Typography variant="body1" fontWeight={500} color="primary">{note.patient.bp}</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle2" color="text.secondary">Weight</Typography>
              {isEditingPatient ? (
                <TextField
                  fullWidth
                  size="small"
                  value={editedPatient.weight || ''}
                  onChange={(e) => setEditedPatient({ ...editedPatient, weight: e.target.value })}
                />
              ) : (
                <Typography variant="body1">{note.patient.weight}</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle2" color="text.secondary">Pulse</Typography>
              {isEditingPatient ? (
                <TextField
                  fullWidth
                  size="small"
                  value={editedPatient.pulse || ''}
                  onChange={(e) => setEditedPatient({ ...editedPatient, pulse: e.target.value })}
                />
              ) : (
                <Typography variant="body1">{note.patient.pulse} bpm</Typography>
              )}
            </Grid>
            {isEditingPatient && (
              <>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" color="text.secondary">Height</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={editedPatient.height || ''}
                    onChange={(e) => setEditedPatient({ ...editedPatient, height: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" color="text.secondary">Temp</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={editedPatient.temperature || ''}
                    onChange={(e) => setEditedPatient({ ...editedPatient, temperature: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" color="text.secondary">RR</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={editedPatient.respiratory_rate || ''}
                    onChange={(e) => setEditedPatient({ ...editedPatient, respiratory_rate: e.target.value })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {(note.custom_fields?.protocol_adherence || []).length > 0 && (
        <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'primary.main' }}>
          <CardHeader title="Protocol adherence"
            subheader="AI assessment of how this visit followed the attached protocol" />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {note.custom_fields.protocol_adherence.map((a, i) => {
                const st = (a.status || '').toLowerCase();
                const color = st === 'followed' ? 'success' : st === 'deviated' ? 'warning' : 'default';
                const label = st === 'followed' ? '✓ Followed' : st === 'deviated' ? '⚠ Deviated' : 'Not addressed';
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Chip size="small" color={color} label={label} sx={{ minWidth: 116, fontWeight: 600 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{a.item}</Typography>
                      {a.detail && <Typography variant="body2" color="text.secondary">{a.detail}</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* SOAP Note */}
        <Grid item xs={12} md={note.is_manual ? 12 : 8}>
          <Card>
            <CardHeader
              title="SOAP Note"
              action={
                isEditing ? (
                  <Button startIcon={<SaveIcon />} onClick={handleSaveEdits}>
                    Save
                  </Button>
                ) : canEdit && (
                  <Button startIcon={<EditIcon />} onClick={enterEditMode}>
                    Edit
                  </Button>
                )
              }
            />
            <CardContent>
              {/* Hidden audio element for playback */}
              {audioUrl && (
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  preload="metadata"
                  onLoadedMetadata={handleAudioMetadata}
                  style={{ display: 'none' }}
                />
              )}
              {['subjective', 'objective', 'assessment', 'plan'].map((section) => {
                const isPointsSection = section === 'assessment' || section === 'plan';
                const points = isPointsSection ? getPointsForSection(section) : [];
                const hasStructuredPoints = points.length > 0;

                return (
                  <Box key={section} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {section}
                      </Typography>
                      {/* Speak into this section: record, stop, and the words are
                          added to the end of what is already written. */}
                      {isEditing && (
                        <DictateButton
                          field={`soap-${section}`}
                          busy={dictating}
                          activeField={dictatingField}
                          onText={(t) => appendToSection(section, t)}
                          onRecord={dictateInto}
                          available={canDictate}
                        />
                      )}
                      {isEditing && dictatingField === `soap-${section}` && (
                        <Typography variant="caption" color="error.main">
                          Recording — press stop to add it to {section}
                        </Typography>
                      )}
                    </Box>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={section === 'subjective' || section === 'objective' ? 4 : 6}
                        value={editedSoap[section] || ''}
                        onChange={(e) => setEditedSoap({ ...editedSoap, [section]: e.target.value })}
                        placeholder={note.is_manual ? `Type the ${section}, or press the microphone to dictate it` : undefined}
                        variant="outlined"
                      />
                    ) : hasStructuredPoints ? (
                      <>
                      <List dense disablePadding>
                        {points.map((pt, idx) => {
                          const hasTimestamp = typeof pt.start_sec === 'number' && typeof pt.end_sec === 'number';
                          const isPlaying = playingSegment && playingSegment.start === pt.start_sec;
                          return (
                            <ListItem key={idx} sx={{ alignItems: 'flex-start', py: 0.5 }}>
                              <Box sx={{ width: audioUrl ? 32 : 0, flexShrink: 0 }}>
                                {hasTimestamp && audioUrl && (
                                  <IconButton
                                    size="small"
                                    onClick={() => playSegment(pt.start_sec, pt.end_sec)}
                                    sx={{ mt: -0.5, ml: -1 }}
                                    color={isPlaying ? 'primary' : 'default'}
                                  >
                                    <PlayIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              <ListItemText
                                primary={pt.text}
                                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                      {/* The points above are for review; the paragraph below is the
                          field the patient actually reads in their portal. They are
                          generated separately, so show both — otherwise wording can
                          reach the patient that the provider never saw. */}
                      {note.soap[section] && (
                        <Box sx={{ mt: 1, ml: audioUrl ? 4 : 2, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                            The patient reads this wording — edit to change it
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {note.soap[section]}
                          </Typography>
                        </Box>
                      )}
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="pre"
                        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                      >
                        {note.soap[section]}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </CardContent>
          </Card>

          {/* ICD-10 Codes */}
          <Card sx={{ mt: 2 }}>
            <CardHeader
              title={note.is_manual ? 'ICD-10 Codes' : 'Recommended ICD-10 Codes'}
              subheader={note.is_manual
                ? 'Add the codes for this visit.'
                : 'AI-recommended from this visit — review and confirm before finalizing.'}
              action={
                isEditing && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddIcd10Dialog(true)}
                  >
                    Add code
                  </Button>
                )
              }
            />
            <CardContent>
              {(isEditing ? editedIcd10Codes : note.icd10_codes || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No codes recommended{isEditing ? ' — use “Add code” to include one.' : '.'}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(isEditing ? editedIcd10Codes : note.icd10_codes || []).map((code, idx) => (
                    <Chip
                      key={idx}
                      label={`${code.code} - ${code.description || ''}`}
                      color="primary"
                      variant="outlined"
                      onDelete={isEditing ? () => handleRemoveIcd10(idx) : undefined}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <Dialog open={openAddIcd10Dialog} onClose={() => setOpenAddIcd10Dialog(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Add ICD-10 Code</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Code"
                placeholder="e.g. E11.9"
                fullWidth
                value={newIcd10Code}
                onChange={(e) => setNewIcd10Code(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Description"
                placeholder="e.g. Type 2 diabetes"
                fullWidth
                value={newIcd10Desc}
                onChange={(e) => setNewIcd10Desc(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenAddIcd10Dialog(false)}>Cancel</Button>
              <Button onClick={handleAddIcd10} variant="contained">
                Add
              </Button>
            </DialogActions>
          </Dialog>

          {/* Prescriptions */}
          <Card sx={{ mt: 2 }}>
            <CardHeader
              title="Draft Prescriptions"
              subheader={isEditing ? 'Name, dose, how often, before or after food, and for how long. These go to the patient when you finalize.' : undefined}
              avatar={<MedIcon color="primary" />}
              action={isEditing && (
                <Button size="small" startIcon={<AddIcon />} onClick={addPrescription}>
                  Add medication
                </Button>
              )}
            />
            <CardContent>
              {isEditing ? (
                editedPrescriptions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No medications drafted. Add one above.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {editedPrescriptions.map((rx, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              fullWidth size="small" label="Medication"
                              value={rx.medication || ''}
                              onChange={(e) => updatePrescription(idx, 'medication', e.target.value)}
                            />
                            <DictateButton
                              field={`med-${idx}`}
                              busy={dictating}
                              activeField={dictatingField}
                              onText={(t) => appendToPrescription(idx, 'medication', t)}
                              onRecord={dictateInto}
                              available={canDictate}
                            />
                          </Box>
                          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1.4fr 1.3fr 1fr' } }}>
                            <TextField
                              size="small" label="Dose" placeholder="1 tablet"
                              value={rx.dose || ''}
                              onChange={(e) => updatePrescription(idx, 'dose', e.target.value)}
                            />
                            <Autocomplete
                              freeSolo size="small" options={RX_FREQUENCIES}
                              value={rx.frequency || ''}
                              inputValue={rx.frequency || ''}
                              onInputChange={(_, v) => updatePrescription(idx, 'frequency', v)}
                              renderInput={(params) => <TextField {...params} label="How often" placeholder="Twice a day" />}
                            />
                            <TextField
                              select size="small" label="When"
                              value={rx.timing || ''}
                              onChange={(e) => updatePrescription(idx, 'timing', e.target.value)}
                            >
                              {RX_TIMINGS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                            </TextField>
                            <TextField
                              size="small" label="For how long" placeholder="5 days"
                              value={rx.duration || ''}
                              onChange={(e) => updatePrescription(idx, 'duration', e.target.value)}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              fullWidth size="small" label="Other instructions"
                              placeholder="Finish the course even if you feel better"
                              value={rx.notes || ''}
                              onChange={(e) => updatePrescription(idx, 'notes', e.target.value)}
                            />
                            <DictateButton
                              field={`notes-${idx}`}
                              busy={dictating}
                              activeField={dictatingField}
                              onText={(t) => appendToPrescription(idx, 'notes', t)}
                              onRecord={dictateInto}
                              available={canDictate}
                            />
                          </Box>
                        </Box>
                        <IconButton onClick={() => removePrescription(idx)} sx={{ mt: 0.5 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )
              ) : note.prescriptions_draft?.length ? (
                <List>
                  {note.prescriptions_draft.map((rx, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={rx.medication}
                        secondary={<RxDirections rx={rx} />}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No medications drafted for this visit.
                </Typography>
              )}
            </CardContent>
          </Card>
          {/* Follow-up: the patient is emailed three days before, and the day before. */}
          <Card sx={{ mt: 2 }}>
            <CardHeader
              title="Follow-up"
              subheader={isEditing
                ? 'When should the patient come back? They are emailed a reminder three days before, and the day before.'
                : undefined}
              avatar={<FollowUpIcon color="primary" />}
            />
            <CardContent>
              {isEditing ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <TextField
                    type="date" size="small" label="Follow-up date"
                    value={editedFollowUp}
                    onChange={(e) => setEditedFollowUp(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: note.visit_date ? addDays(note.visit_date, 1) : undefined }}
                  />
                  {note.visit_date && FOLLOW_UP_PICKS.map((p) => {
                    const day = addDays(note.visit_date, p.days);
                    return (
                      <Chip
                        key={p.label} label={`In ${p.label}`} clickable
                        color={editedFollowUp === day ? 'primary' : 'default'}
                        variant={editedFollowUp === day ? 'filled' : 'outlined'}
                        onClick={() => setEditedFollowUp(day)}
                      />
                    );
                  })}
                  {editedFollowUp && (
                    <Button size="small" onClick={() => setEditedFollowUp('')}>No follow-up</Button>
                  )}
                </Box>
              ) : note.follow_up_date ? (
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{longDay(note.follow_up_date)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    The patient is emailed a reminder three days before, and the day before.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No follow-up set for this visit.</Typography>
              )}
            </CardContent>
          </Card>
          {/* Reports: this visit's, then the patient's others at this office. */}
          <Card sx={{ mt: 2 }}>
            <CardHeader
              title="Reports"
              subheader="Blood tests, scans and letters for this patient."
              avatar={<ReportIcon color="primary" />}
              action={note.patient?.patient_id && (
                <Button size="small" startIcon={<AddReportIcon />} onClick={() => setAddingReport(true)}>
                  Add report
                </Button>
              )}
            />
            <CardContent sx={{ pt: 0 }}>
              {patientReports.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No reports for this patient yet.
                </Typography>
              ) : (
                [
                  ['From this visit', patientReports.filter((r) => r.session_id === sessionId)],
                  ['Other reports', patientReports.filter((r) => r.session_id !== sessionId)],
                ].filter(([, rows]) => rows.length).map(([label, rows]) => (
                  <Box key={label} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
                      {label.toUpperCase()}
                    </Typography>
                    <List dense disablePadding>
                      {rows.map((r) => (
                        <ListItem key={r.id} disableGutters
                          secondaryAction={(
                            <Button size="small" onClick={() => openReportFile(`${API_URL}/api/v1/reports/${r.id}/file`)}>
                              Open
                            </Button>
                          )}>
                          <ListItemText
                            primary={r.title}
                            secondary={[
                              reportKindLabel(r),
                              r.report_date || r.visit_date || (r.created_at || '').slice(0, 10),
                              r.uploaded_by_role === 'patient' ? 'added by the patient' : (r.uploaded_by_name ? `added by ${r.uploaded_by_name}` : null),
                            ].filter(Boolean).join(' · ')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
          {note.patient?.patient_id && (
            <ReportUpload
              open={addingReport}
              onClose={() => setAddingReport(false)}
              onSaved={() => loadReports(note.patient.patient_id)}
              patient={{ id: note.patient.patient_id, name: note.patient.name }}
              sessionId={sessionId}
            />
          )}
        </Grid>

        {/* Transcript — there is none for a visit written by hand. */}
        {!note.is_manual && (
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Transcript" />
            <CardContent sx={{ maxHeight: 600, overflow: 'auto' }}>
              {transcript.map((segment, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={segment.speaker}
                      color={segment.speaker === 'provider' ? 'primary' : 'default'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {segment.start_sec}s - {segment.end_sec}s
                    </Typography>
                    {audioUrl && (
                      <IconButton
                        size="small"
                        onClick={() => playSegment(segment.start_sec, segment.end_sec)}
                      >
                        <PlayIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="body2">{segment.text}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/patients')}>
          {isFinalized ? 'Back to Patients' : 'Cancel'}
        </Button>
        {isFinalized ? (
          <Typography variant="body2" color="text.secondary">
            This visit is finalized — the recording was deleted and the note is locked.
          </Typography>
        ) : !canEdit ? (
          <Typography variant="body2" color="text.secondary">
            Only the clinician who recorded this visit can edit or finalize it.
          </Typography>
        ) : (
          <Button
            variant="contained"
            startIcon={finalizing ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleFinalize}
            disabled={finalizing}
          >
            {finalizing ? 'Finalizing…' : 'Finalize Note'}
          </Button>
        )}
      </Box>

      {/* Finalizing runs three irreversible steps server-side (approve, write the
          patient summary, delete the recording), so it can take a few seconds.
          Hold the screen rather than let the provider wonder if the click landed. */}
      {finalizing && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 1400,
          bgcolor: 'rgba(255,255,255,0.94)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5,
        }}>
          <Box component="svg" viewBox="0 0 240 80" sx={{ width: 240, height: 80 }}>
            {/* Faint full trace, so the sweeping line has a track to run along. */}
            <Box
              component="path"
              d={ECG_PATH}
              fill="none" stroke="#0d9488" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
              sx={{
                '@keyframes ecgGlow': { '0%, 100%': { opacity: 0.2 }, '50%': { opacity: 0.45 } },
                animation: 'ecgGlow 1.6s ease-in-out infinite',
              }}
            />
            <Box
              component="path"
              d={ECG_PATH}
              fill="none" stroke="#0d9488" strokeWidth="3"
              strokeLinecap="round" strokeLinejoin="round"
              sx={{
                '@keyframes ecgSweep': { from: { strokeDashoffset: 640 }, to: { strokeDashoffset: -640 } },
                strokeDasharray: 640,
                animation: 'ecgSweep 1.8s linear infinite',
              }}
            />
          </Box>
          <Typography variant="h6" fontWeight={700}>Finalizing visit…</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, textAlign: 'center' }}>
            Approving the note, writing the patient's summary and deleting the recording.
            Please don't close this tab.
          </Typography>
        </Box>
      )}

    </Box>
  );
}
